import { useNavigate } from 'react-router-dom'
import HomeworkCard from './HomeworkCard'
import HorizontalScroller from './HorizontalScroller'
import { type HomeworkSubmission, type Homework, type HomeworkSelection } from '../services/homework'
import submissionIconMark from '../assets/icon/submission-icon-mark.svg'

interface Props {
  schoolHomeworks: Homework[]
  entreHomeworks: Homework[]
  showEntreCard: boolean
  systemSelection: HomeworkSelection | null
  submissions: HomeworkSubmission[]
  headerClassName?: string
  cardRowClassName?: string
}

type CardType = 'school' | 'entre' | 'system'

interface CurrentHomeworkItem {
  cardType: CardType
  chipLabel: string
  homework: Homework
}

// 締切が同じときの並び順
const TYPE_ORDER: Record<CardType, number> = { school: 0, entre: 1, system: 2 }

// 締切が近い順。締切なしは最後、同じ締切なら 学校 → アントレ部 → システム → 作成順
const compareItems = (a: CurrentHomeworkItem, b: CurrentHomeworkItem) => {
  const da = a.homework.deadline_at
  const db = b.homework.deadline_at
  if (da !== db) {
    if (!da) return 1
    if (!db) return -1
    const diff = Date.parse(da) - Date.parse(db)
    if (diff !== 0) return diff
  }
  return TYPE_ORDER[a.cardType] - TYPE_ORDER[b.cardType] ||
    a.homework.created_at.localeCompare(b.homework.created_at)
}

const CurrentHomeworkSection = ({
  schoolHomeworks,
  entreHomeworks,
  showEntreCard,
  systemSelection,
  submissions,
  headerClassName = 'flex items-center gap-2',
  cardRowClassName = 'flex gap-3 overflow-x-auto pb-2 scrollbar-hide',
}: Props) => {
  const navigate = useNavigate()

  // システム宿題は生徒が選んだものだけ。選んだ後に提出したもの（採点待ち）は出さない
  // （採点されると選択が消える。以前に提出した宿題を選び直した場合は出す）
  const system = systemSelection?.homework
  const systemSubmitted = !!system && submissions.some(
    s => s.homework_id === system.id && s.submitted_at >= systemSelection.selected_at
  )

  const items: CurrentHomeworkItem[] = [
    ...schoolHomeworks.map(homework => ({ cardType: 'school' as const, chipLabel: '学校', homework })),
    ...(showEntreCard
      ? entreHomeworks.map(homework => ({ cardType: 'entre' as const, chipLabel: 'アントレ部', homework }))
      : []),
    ...(system && !systemSubmitted
      ? [{ cardType: 'system' as const, chipLabel: system.category ?? 'キャリア', homework: system }]
      : []),
  ].sort(compareItems)

  // 宿題が何もない時はセクションごと非表示にする
  if (items.length === 0) {
    return null
  }

  return (
    <>
      <div className={headerClassName}>
        <img alt="" className="shrink-0" src={submissionIconMark} style={{ width: 45, height: 40 }} />
        <span className="text-white text-xl font-bold">現在の宿題</span>
      </div>

      {/* 3つを超える分は横スクロール（右の矢印でも送れる） */}
      <HorizontalScroller className={cardRowClassName} arrowInsetClassName={{ left: '-left-2', right: '-right-2' }}>
        {items.map(({ cardType, chipLabel, homework }) => (
          <HomeworkCard
            key={homework.id}
            cardType={cardType} chipLabel={chipLabel}
            title={homework.title} deadlineAt={homework.deadline_at ?? null}
            onClick={() => navigate(`/homework/${homework.id}`)}
          />
        ))}
      </HorizontalScroller>
    </>
  )
}

export default CurrentHomeworkSection
